import { BeckyModel } from '@wix/thunderbolt-becky-root'
import { MegaStore, singleTransaction } from '@wix/thunderbolt-catharsis'
import { shapeIdsDataNodes } from '@wix/thunderbolt-fetch-shapes'
import {
	stringifyAllComponentsCss,
	getComponentsCss,
	componentCssFeaturesProvider,
	viewerCssFeatures,
} from '@wix/thunderbolt-catharsis-extensions'
import _ from 'lodash'
import { createCatharsisArgs } from './createCatharsisArgs'
import type { RefSchemas } from '@wix/thunderbolt-becky-types'

const responsiveMasterPageFeatures = {
	fontFaces: viewerCssFeatures.fontFaces,
	accessibility: viewerCssFeatures.accessibility,
	themeFontsAndColors: viewerCssFeatures.themeFontsAndColors,
}

export const getCatharsisCss = (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T
): MegaStore => {
	const { isResponsive, pageCompId } = beckyModel
	const isResponsiveMasterPage = isResponsive && pageCompId === 'masterPage'

	const { componentNodes, usedDataMaps } = runAndReport(
		() =>
			componentCssFeaturesProvider(
				isResponsiveMasterPage ? responsiveMasterPageFeatures : viewerCssFeatures,
				(exp) => !!beckyModel.experiments[exp],
				beckyModel
			),
		'componentCssFeaturesProvider'
	)

	const catharsisMegaStore = runAndReport(() => new MegaStore(), 'MegaStore')
	const catharsisArgs = runAndReport(
		() => createCatharsisArgs(catharsisMegaStore, componentNodes, shapeIdsDataNodes, schemas),
		'createCatharsisArgs'
	)

	runAndReport(() => singleTransaction(beckyModel, catharsisArgs, usedDataMaps, runAndReport), 'singleTransaction')

	return catharsisMegaStore
}

export const getCatharsisCssString = (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T
): string => {
	const catharsisMegaStore = runAndReport(() => getCatharsisCss(beckyModel, schemas, runAndReport), 'getCatharsisCss')
	const { pageCompId, viewMode, experiments } = beckyModel
	const isMobileView = viewMode === 'mobile'

	const allComponentsCss = runAndReport(
		() => getComponentsCss(catharsisMegaStore, pageCompId, isMobileView, experiments),
		'componentsCss'
	)
	const componentsCss = runAndReport(() => stringifyAllComponentsCss(allComponentsCss), 'stringifyAllComponentsCss')

	const allFontFacesCss = runAndReport(
		() => catharsisMegaStore.getById<Record<string, Array<string>>>('fontFacesCss'),
		'catharsisMegaStore.getById'
	)

	const getFontCss = () => {
		return _(allFontFacesCss)
			.flatMap((fontFacesCss) => fontFacesCss)
			.uniq()
			.value()
	}
	const fontsCss = runAndReport(() => getFontCss(), 'getFontCss')

	const css = runAndReport(
		() =>
			[
				/* catharsis font faces */
				...fontsCss,
				/* catharsis components css */
				componentsCss,
			].join('\n'),
		'concat all css'
	)

	return css
}
